import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box } from '@atlaskit/primitives';
import { ElementName } from '@atlaskit/smart-card';
import { cssMap } from '@atlaskit/css';
import type { AtlasProjectGroupHeader$key } from '@atlassian/jira-relay/src/__generated__/AtlasProjectGroupHeader.graphql';
import { useIntl } from '@atlassian/jira-intl';
import { SimpleInlineSmartLink } from '@atlassian/jira-issue-field-atlas-project-common/src/ui/atlas-project-smart-link/inline-smart-llink/index.tsx';
import { useProjectUrlConstructor } from '@atlassian/jira-issue-field-atlas-project-common/src/util/index.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	textStyles: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

export type Props = {
	atlasProjectGroupHeader: AtlasProjectGroupHeader$key | null;
};

export const AtlasProjectGroupHeader = ({ atlasProjectGroupHeader }: Props) => {
	const data = useFragment<AtlasProjectGroupHeader$key>(
		graphql`
			fragment AtlasProjectGroupHeader on JiraJqlTownsquareProjectFieldValue {
				project {
					id
					key
					privateProject
					hasPermission
					name
				}
			}
		`,
		atlasProjectGroupHeader,
	);

	const { formatMessage } = useIntl();
	const constructAtlasProjectUrl = useProjectUrlConstructor();

	if (data?.project) {
		const url = constructAtlasProjectUrl({
			ari: data?.project?.id ?? '',
			projectKey: data?.project?.key ?? '',
		});

		return (
			<SimpleInlineSmartLink
				url={url}
				text={data?.project?.name ?? ''}
				metadata={[{ name: ElementName.State }]}
				projectAri={data?.project?.id ?? ''}
				isPrivate={data?.project?.privateProject ?? false}
				initialHasNoAccess={data?.project?.hasPermission === false}
			/>
		);
	}

	return <Box xcss={styles.textStyles}>{formatMessage(messages.noAtlasProjectDisplayName)}</Box>;
};
